import { listToProseString } from 'components/members/utils'
import { type GrapeMoney, type MoneyAmount } from 'types'
import { type UserFacingEnrollmentMetadata } from 'types/member_onboarding_v2'
import { convertGrapeMoneyToNumber, formatMoney } from 'utils'
import {
  type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansMetalTierItem as MetalTier,
  type MemberOnboardingEnrollmentEvent,
  type MemberOnboardingMemberPlanChangesProps,
  type MemberOnboardingPlanYearSummaryCoveredParticipantsItem,
  type Money,
} from 'utils/web_api.schemas'

import { type PlanGranularFiltersTypes } from './components/PlanGranularFilters'
import {
  QLE_DOCUMENT_UPLOAD_COPY,
  type QleDocumentUploadCopyConfig,
} from './config/qleDocumentUploadCopy'
import {
  type MedicalPlan,
  type MedicalPlanDisplayItem,
} from './medicalPlanTypes'
import {
  type OnboardingAllowance,
  type OnboardingEnrollment,
  type OnboardingInfo,
  type PlanCardBadgeCategory,
} from './types'

export const ALERA_MEDICARE_FORM_URL = 'https://form.typeform.com/to/SToCnSgt'
export const MEDICARE_FORM_URL = 'https://form.typeform.com/to/GPADGMQp'

const OFF_THATCH_OR_MEDICARE_CATEGORIES: readonly string[] = [
  'off_thatch_medical',
  'off_thatch_dental',
  'off_thatch_vision',
]

/**
 * Formats a masked SSN for display as •••—••—XXXX (em dash).
 * Handles API format XXX-XX-1234 (X for masked) and numeric 123456789; only the last 4 digits are shown.
 */
export const formatMaskedSSNForDisplay = (maskedSsn: string): string => {
  if (!maskedSsn) return ''
  const digitsOnly = maskedSsn.replace(/\D/g, '')
  const last4 = digitsOnly.slice(-4)
  if (last4.length < 4) return maskedSsn
  return `•••—••—${last4}`
}

const DEDUCTIBLE_LARGER_INFO =
  "You'll pay more out of pocket before your plan starts covering care. Best for low-usage or emergency-only needs"
const DEDUCTIBLE_SMALLER_INFO =
  'Starts covering care sooner. Good if you expect to use more services'
const DEDUCTIBLE_INFO =
  "The amount you'll pay out of pocket before your plan starts covering care"
export const MOOP_INFO =
  "The most you'll have to pay in a year for covered care, including your deductible, copays, and coinsurance"
export const PRIMARY_DOCTOR_VISITS_INFO =
  "What you'll pay when you see a regular medical provider. Copays are flat fees; coinsurance is a percentage of the total cost"
export const SPECIALIST_DOCTOR_VISITS_INFO =
  "What you'll pay when you see a specialized medical practitioner, like a cardiologist or dermatologist. " +
  'Copays are flat fees; coinsurance is a percentage of the total cost'

export const getDeductibleTooltipLabel = (
  parsedDeductibleValue: number | string,
  isIndividual: boolean
) => {
  if (
    typeof parsedDeductibleValue === 'string' ||
    parsedDeductibleValue === null
  ) {
    return DEDUCTIBLE_INFO
  }
  if (isIndividual) {
    return parsedDeductibleValue >= 1650
      ? DEDUCTIBLE_LARGER_INFO
      : DEDUCTIBLE_SMALLER_INFO
  } else {
    return parsedDeductibleValue >= 3300
      ? DEDUCTIBLE_LARGER_INFO
      : DEDUCTIBLE_SMALLER_INFO
  }
}

const LOWEST_COST_TO_YOU =
  'Based on your inputs, this plan has the lowest estimated total cost and matches your expected care needs'
const MOST_COMPREHENSIVE =
  'Based on your inputs, this plan covers the most while staying within your budget'
const RENEWAL_PLAN =
  'This is the plan that you will get if you renew your previous plan'

export const getRecommendationReasonLabels = (
  category: PlanCardBadgeCategory
) => {
  if (category === 'lowest_cost') {
    return {
      cardLabel: 'Lowest cost plan',
      tableLabel: 'Lowest cost',
      tooltipLabel: LOWEST_COST_TO_YOU,
    }
  } else if (category === 'renewal') {
    return {
      cardLabel: 'Your current plan',
      tableLabel: 'Current plan',
      tooltipLabel: RENEWAL_PLAN,
    }
  } else {
    return {
      cardLabel: 'Most comprehensive plan',
      tableLabel: 'Most comprehensive',
      tooltipLabel: MOST_COMPREHENSIVE,
    }
  }
}

export const hasExistingOnThatchEnrollments = (
  onboardingData: OnboardingInfo | null | undefined
) => {
  const onThatchEnrollments = (onboardingData?.enrollments || []).filter(
    (e: OnboardingEnrollment) => {
      return (
        e.covers_vision ||
        e.covers_dental ||
        (e.covers_medical && e.payment_method === 'paid_by_thatch')
      )
    }
  )
  return onThatchEnrollments.length > 0
}

export const allowanceIsEmpty = (allowance?: OnboardingAllowance | null) => {
  return (
    !allowance ||
    (allowance.allowance_amount === null && allowance.qme_treatment === null)
  )
}

export const generateRandomPhoneNumber = () => {
  const areaCode = Math.floor(200 + Math.random() * 800) // 200-999
  const firstPart = Math.floor(100 + Math.random() * 900) // 100-999
  const secondPart = Math.floor(1000 + Math.random() * 9000) // 1000-9999
  return `(${areaCode}) ${firstPart}-${secondPart}`
}

export const extractInNetworkString = (
  benefit: string | null | undefined
): string => {
  if (!benefit) {
    return 'Not Available'
  }

  const valueRegex = /^In-Network: (.*) \/ /
  const match = benefit.match(valueRegex)

  if (!match?.[1]) {
    return 'Not Available'
  }

  return match[1]
}

export const extractInNetworkAmount = (
  benefit: string | null | undefined
): number | string => {
  if (!benefit) {
    return 'Not Available'
  }

  const valueRegex = /^In-Network: \$([\d,.]+)/
  const match = benefit.match(valueRegex)

  if (!match?.[1]) {
    // fall back to extractInNetworkString if no match
    return extractInNetworkString(benefit)
  }

  // Remove commas and convert to number
  return Number(match[1].replace(/,/g, ''))
}

export const extractOutOfNetworkString = (
  benefit: string | null | undefined
): string => {
  if (!benefit) {
    return 'Not Available'
  }

  const valueRegex = /(?<=Out-of-Network: )(.*?)(?=\s*\||$)/
  const match = benefit.match(valueRegex)

  if (!match?.[1]) {
    return 'Not Available'
  }

  return match[1]
}

export const showThisRecommendedPlan = (
  medicalPlanData: MedicalPlan,
  filters: PlanGranularFiltersTypes
) => {
  const metalTierFilters =
    filters.metalTiers.length > 0 ? filters.metalTiers : undefined
  const carrierFilters =
    filters.carriers.length > 0 ? filters.carriers : undefined
  const planTypeFilters =
    filters.planTypes.length > 0 ? filters.planTypes : undefined

  if (!metalTierFilters && !carrierFilters && !planTypeFilters) {
    // If there are no filters, we can show this plan right away
    return true
  }

  if (
    metalTierFilters &&
    !metalTierFilters.includes(medicalPlanData.metal_tier)
  ) {
    return false
  }

  if (
    carrierFilters &&
    !carrierFilters.includes(medicalPlanData.carrier_name)
  ) {
    return false
  }

  if (planTypeFilters && !planTypeFilters.includes(medicalPlanData.plan_type)) {
    return false
  }

  return true
}

export const calculateRemainingBudgetForTieredAllowance = ({
  planDisplayItem,
}: {
  planDisplayItem: MedicalPlanDisplayItem;
}) => {
  const remaining =
    planDisplayItem.tiered_allowance_remaining_budget?.subunits || 0
  return remaining < 0
    ? convertGrapeMoneyToNumber(
      planDisplayItem.tiered_allowance_remaining_budget as GrapeMoney
    )
    : 0
}

// Helper functions to normalize different data types to subunits
const normalizeMoneyAmount = (
  amount?: GrapeMoney | MoneyAmount | Money | null
): number => {
  if (!amount) return 0

  if ('subunits' in amount) {
    return (amount as GrapeMoney).subunits || 0
  } else {
    return parseFloat((amount as MoneyAmount).value || '0') * 100
  }
}

const normalizeQuotedPremium = (
  medicalEnrollment?:
    | OnboardingEnrollment
    | UserFacingEnrollmentMetadata
    | null,
  medicalPlanDisplayItem?: MedicalPlanDisplayItem | null
): number => {
  if (medicalEnrollment) {
    if (
      'quoted_premium' in medicalEnrollment &&
      medicalEnrollment.quoted_premium
    ) {
      return (medicalEnrollment.quoted_premium as GrapeMoney).subunits || 0
    } else if (
      'premium_amount' in medicalEnrollment &&
      medicalEnrollment.premium_amount
    ) {
      return (
        parseFloat(
          (medicalEnrollment.premium_amount as MoneyAmount).value || '0'
        ) * 100
      )
    }
    return 0
  }

  return medicalPlanDisplayItem?.total_premium?.subunits || 0
}

export const calculateTieredAllowanceBreakdown = ({
  allowanceAmount,
  medicalEnrollment,
  medicalPlanDisplayItem,
}: {
  allowanceAmount?: GrapeMoney | MoneyAmount | null;
  medicalEnrollment?:
    | OnboardingEnrollment
    | UserFacingEnrollmentMetadata
    | null;
  medicalPlanDisplayItem?: MedicalPlanDisplayItem | null;
}) => {
  const maxPremiumReimbursementAmount = medicalEnrollment
    ? medicalEnrollment?.max_premium_reimbursement_amount
    : medicalPlanDisplayItem?.tiered_allowance_employee_only_premium

  // Normalize all values to subunits
  const allowanceAmountSubunits = normalizeMoneyAmount(allowanceAmount)
  const maxPremiumReimbursementAmountSubunits = normalizeMoneyAmount(
    maxPremiumReimbursementAmount
  )
  const quotedPremiumTotalSubunits = normalizeQuotedPremium(
    medicalEnrollment,
    medicalPlanDisplayItem
  )

  // Perform the calculation with normalized data
  const employerContribution = Math.min(
    allowanceAmountSubunits,
    maxPremiumReimbursementAmountSubunits
  )

  const employeeContributionSubunits =
    quotedPremiumTotalSubunits - employerContribution

  return {
    subunits: employeeContributionSubunits,
    rawAmount: employeeContributionSubunits / 100,
    formattedAmount: formatMoney(employeeContributionSubunits / 100),
    formattedEmployerContribution: formatMoney(employerContribution / 100),
  }
}

// Define metal tier order for sorting
export const METAL_TIER_ORDER: Record<MetalTier, number> = {
  platinum: 0,
  gold: 1,
  silver: 2,
  expanded_bronze: 3,
  bronze: 4,
  catastrophic: 5,
}

/**
 * Sorts metal tiers in the order: platinum > gold > silver > expanded_bronze > bronze > catastrophic
 */
export const sortMetalTiers = (metalTiers: MetalTier[]): MetalTier[] => {
  return [...metalTiers].sort((a, b) => {
    const orderA = METAL_TIER_ORDER[a] ?? 999
    const orderB = METAL_TIER_ORDER[b] ?? 999
    return orderA - orderB
  })
}

// Type for objects that have enrollment event-like properties (type and subtype)
// This allows passing either a full MemberOnboardingEnrollmentEvent or a minimal object with just type/subtype
export type EnrollmentEventLike =
  | MemberOnboardingEnrollmentEvent
  | {
    type?: string;
    subtype?: string;
  }
  | null
  | undefined

export const isEnrollmentEventRenewal = (enrollmentEvent?: MemberOnboardingEnrollmentEvent | EnrollmentEventLike): boolean => {
  return enrollmentEvent?.type === 'open_enrollment' && enrollmentEvent?.subtype === 'renewal'
}

export const isEnrollmentEventQle = (enrollmentEvent?: MemberOnboardingEnrollmentEvent | EnrollmentEventLike): boolean => {
  return enrollmentEvent?.type === 'special_enrollment'
}

export const isEnrollmentEventICHRAInvite = (enrollmentEvent?: MemberOnboardingEnrollmentEvent | EnrollmentEventLike): boolean => {
  return enrollmentEvent?.type === 'ichra_invite'
}

export const shouldSkipQleDocumentUpload = (enrollmentEvent?: MemberOnboardingEnrollmentEvent | EnrollmentEventLike): boolean => {
  const enrollmentSubtype = enrollmentEvent?.subtype
  return enrollmentSubtype === 'medicare' || enrollmentSubtype === 'thatch_incident'
}

export const isRenewal = (data: OnboardingInfo): boolean => {
  return isEnrollmentEventRenewal(data.enrollment_event)
}

export const isQle = (data: OnboardingInfo): boolean => {
  return isEnrollmentEventQle(data.enrollment_event)
}

export const isNewQleFlow = (
  data: OnboardingInfo,
  newQleEnabled: boolean | null = false
): boolean => {
  return isEnrollmentEventQle(data.enrollment_event) && !!newQleEnabled
}

export const isRenewalOrQleFlow = (
  data: OnboardingInfo,
  newQleEnabled: boolean | null = false
): boolean => {
  return isEnrollmentEventRenewal(data.enrollment_event) || (isEnrollmentEventQle(data.enrollment_event) && !!newQleEnabled)
}

export const isInitialICHRAInvite = (data: OnboardingInfo): boolean => {
  return isEnrollmentEventICHRAInvite(data.enrollment_event)
}

/**
 * Checks if two medical plan codes represent the same plan across different years.
 * Plan codes follow the format: mdpln_YYYY-{suffix}
 * Plans are considered the same if they have the same suffix after the year (case insensitive).
 *
 * @example
 * isSameMedicalPlanAcrossYears('mdpln_2025-ca-40513ca0390014', 'mdpln_2026-ca-40513ca0390014') // true
 * isSameMedicalPlanAcrossYears('mdpln_2025-ca-40513ca0390014', 'mdpln_2026-ca-40513CA0390014') // true (case insensitive)
 * isSameMedicalPlanAcrossYears('mdpln_2025-ca-40513ca0390014', 'mdpln_2026-ca-different123') // false
 */
export const isSameMedicalPlanAcrossYears = (
  currentCode?: string | null,
  nextCode?: string | null
): boolean => {
  // If either code is null or undefined, return false
  if (!currentCode || !nextCode) {
    return false
  }

  // Check both codes start with mdpln_
  if (!currentCode.startsWith('mdpln_') || !nextCode.startsWith('mdpln_')) {
    return currentCode === nextCode
  }

  // Extract the part after mdpln_YYYY-
  // Format: mdpln_YYYY-{suffix}
  const currentSuffix = currentCode.replace(/^mdpln_\d{4}-/, '')
  const nextSuffix = nextCode.replace(/^mdpln_\d{4}-/, '')

  return currentSuffix.toLowerCase() === nextSuffix.toLowerCase()
}

export const getLabeledPlans = ({
  lowestCostPlan,
  mostComprehensivePlan,
  renewalPlan,
}: {
  lowestCostPlan?: MedicalPlanDisplayItem;
  mostComprehensivePlan?: MedicalPlanDisplayItem;
  renewalPlan?: MedicalPlanDisplayItem;
}) => {
  // Collect all non-null plans with their categories
  const plansWithCategories: Array<{
    plan: MedicalPlanDisplayItem;
    category: PlanCardBadgeCategory;
  }> = []

  if (renewalPlan) {
    plansWithCategories.push({ plan: renewalPlan, category: 'renewal' })
  }
  if (lowestCostPlan) {
    plansWithCategories.push({ plan: lowestCostPlan, category: 'lowest_cost' })
  }
  if (mostComprehensivePlan) {
    plansWithCategories.push({
      plan: mostComprehensivePlan,
      category: 'most_comprehensive',
    })
  }

  // Group plans by their plan ID
  const plansByPlanId = new Map<
    string,
    Array<{
      plan: MedicalPlanDisplayItem;
      category: PlanCardBadgeCategory;
    }>
  >()

  plansWithCategories.forEach(({ plan, category }) => {
    const planId = plan.plan.id
    if (!plansByPlanId.has(planId)) {
      plansByPlanId.set(planId, [])
    }
    plansByPlanId.get(planId)!.push({ plan, category })
  })

  // Convert to final result format
  const result: Array<{
    plan: MedicalPlanDisplayItem;
    planBadgeCategories: PlanCardBadgeCategory[];
  }> = []

  for (const [_planId, planCategories] of plansByPlanId) {
    // Sort categories with 'renewal' first, then 'lowest_cost', then 'most_comprehensive'
    const sortedCategories = planCategories
      .map(({ category }) => category)
      .sort((a, b) => {
        const order = { renewal: 0, lowest_cost: 1, most_comprehensive: 2 }
        return order[a] - order[b]
      })

    result.push({
      plan: planCategories[0].plan, // All plans with same ID are identical
      planBadgeCategories: sortedCategories,
    })
  }

  return result
}

// Guardian's annual max coverage can be returned as .../.../... format
// If the value is supposed to be "Max coverage over 3 years"
export const isMaxCoverageOverThreeYears = (benefit?: string | null) => {
  if (!benefit) return false
  return /^[^/]+\/[^/]+\/[^/]+$/.test(benefit)
}

export const isCurrentPlanOffThatch = (
  planChanges: MemberOnboardingMemberPlanChangesProps
) => {
  return (
    planChanges.current_plan_year.medical_plan?.category ===
      'off_thatch_medical' ||
    planChanges.current_plan_year.medical_plan?.category.startsWith('medicare')
  )
}

export const isCurrentDentalPlanOffThatch = (
  planChanges: MemberOnboardingMemberPlanChangesProps
) => {
  return (
    planChanges.current_plan_year.dental_plan?.category === 'off_thatch_dental'
  )
}

export const isCurrentVisionPlanOffThatch = (
  planChanges: MemberOnboardingMemberPlanChangesProps
) => {
  return (
    planChanges.current_plan_year.vision_plan?.category === 'off_thatch_vision'
  )
}

export const isNextPlanEmpty = (
  planChanges: MemberOnboardingMemberPlanChangesProps
) => {
  return !planChanges.next_plan_year.medical_plan
}

export const areCoveredParticipantsDifferent = ({
  overallCoveredParticipantIds,
  planCoveredParticipantIds,
}: {
  overallCoveredParticipantIds: string[];
  planCoveredParticipantIds: string[];
}) => {
  return (
    overallCoveredParticipantIds.length !== planCoveredParticipantIds.length ||
    overallCoveredParticipantIds.some(
      (id) => !planCoveredParticipantIds.includes(id)
    )
  )
}

export const getCoveredParticipantsForAncillaryPlan = ({
  overallCoveredParticipants,
  planCoveredParticipantIds,
}: {
  overallCoveredParticipants: MemberOnboardingPlanYearSummaryCoveredParticipantsItem[];
  planCoveredParticipantIds: string[];
}) => {
  const filteredList = overallCoveredParticipants.filter((participant) =>
    planCoveredParticipantIds.includes(participant.id as string)
  )
  const participantNames = filteredList.map(
    (participant) => participant.given_name as string
  )
  return listToProseString(participantNames)
}

/**
 * Checks if all conditions are met to show the QLE show current plan page.
 * This page is shown when:
 * 1. enrollmentEvent is of type 'special_enrollment'
 * 2. covered participants from this year and next year are the same (by ID)
 * 3. Either:
 *    a. no changes to allowance, premiums, and remaining budget AND previous plan and next plan are all the same (and returned!)
 *    OR
 *    b. no changes to allowance (only) AND any plan (medical/dental/vision) is off-thatch or medicare
 */
export const shouldShowQleCurrentPlan = ({
  enrollmentEvent,
  planChangeSummary,
  onboardingData,
}: {
  enrollmentEvent?: MemberOnboardingEnrollmentEvent | null;
  planChangeSummary?: MemberOnboardingMemberPlanChangesProps | null;
  onboardingData?: OnboardingInfo | null;
}): boolean => {
  // Condition 1: enrollmentEvent must be of type 'special_enrollment'
  if (!isEnrollmentEventQle(enrollmentEvent)) {
    return false
  }

  if (!planChangeSummary || !onboardingData) {
    return false
  }

  const currentYear = planChangeSummary.current_plan_year
  const nextYear = planChangeSummary.next_plan_year
  const yoyChange = planChangeSummary.year_over_year_change

  // Condition 2: covered participants from this year and next year must be the same (by ID)
  const currentYearParticipantIds = (currentYear.covered_participants || [])
    .map((p) => p.id as string)
    .filter((id): id is string => id !== null && id !== undefined)
    .sort()

  const nextYearParticipantIds = (nextYear.covered_participants || [])
    .map((p) => p.id as string)
    .filter((id): id is string => id !== null && id !== undefined)
    .sort()

  const participantsAreTheSame =
    currentYearParticipantIds.length === nextYearParticipantIds.length &&
    currentYearParticipantIds.every(
      (id, index) => id === nextYearParticipantIds[index]
    )

  if (!participantsAreTheSame) {
    return false
  }

  // Check if any plan is off-thatch or medicare
  const currentMedicalPlan = currentYear.medical_plan
  const nextMedicalPlan = nextYear.medical_plan
  const currentDentalPlan = currentYear.dental_plan
  const currentVisionPlan = currentYear.vision_plan

  const hasAnyOffThatchPlan = [
    currentMedicalPlan,
    currentDentalPlan,
    currentVisionPlan,
  ].some((plan) => {
    const planCategory = plan?.category
    return (
      (planCategory != null &&
        OFF_THATCH_OR_MEDICARE_CATEGORIES.includes(planCategory)) ||
      (typeof planCategory === 'string' && planCategory.startsWith('medicare'))
    )
  })

  // Alternative path: if participants are the same, no allowance changes, and any plan is off-thatch/medicare
  if (
    participantsAreTheSame &&
    yoyChange.allowance_amount.subunits === 0 &&
    hasAnyOffThatchPlan
  ) {
    return true
  }

  // Condition 3: no changes to allowance, premiums, and remaining budget
  if (
    yoyChange.allowance_amount.subunits !== 0 ||
    yoyChange.premium_total.subunits !== 0 ||
    yoyChange.remaining_balance.subunits !== 0
  ) {
    return false
  }

  // Condition 4: previous plan and next plan are all the same (and returned!)
  // Note: currentMedicalPlan, currentDentalPlan, and currentVisionPlan are already defined above
  const nextDentalPlan = nextYear.dental_plan
  const nextVisionPlan = nextYear.vision_plan

  // Check medical plan
  if (currentMedicalPlan && nextMedicalPlan) {
    if (currentMedicalPlan.plan_code !== nextMedicalPlan.plan_code) {
      return false
    }
  } else if (currentMedicalPlan || nextMedicalPlan) {
    // One exists but not the other
    return false
  }

  // Check dental plan
  if (currentDentalPlan && nextDentalPlan) {
    if (currentDentalPlan.plan_code !== nextDentalPlan.plan_code) {
      return false
    }
  } else if (currentDentalPlan || nextDentalPlan) {
    // One exists but not the other
    return false
  }

  // Check vision plan
  if (currentVisionPlan && nextVisionPlan) {
    if (currentVisionPlan.plan_code !== nextVisionPlan.plan_code) {
      return false
    }
  } else if (currentVisionPlan || nextVisionPlan) {
    // One exists but not the other
    return false
  }

  return true
}

export const getSummaryTitleAndDescription = ({
  enrollmentEvent,
}: {
  enrollmentEvent?: MemberOnboardingEnrollmentEvent | null;
}): { title: string; description: string } => {
  if (
    isEnrollmentEventRenewal(enrollmentEvent)
  ) {
    return {
      title: `Your budget for ${enrollmentEvent?.plan_year}`,
      description: `The allowance you're offered and the price of your insurance
            coverage can change every year — see what that means for your bottom
            line:`,
    }
  }
  // for QLE
  // TODO: once we support address change, we would need to take account of that for the descriptions
  return {
    title: 'Your new budget',
    description: `The allowance you're offered and the price of your insurance
            coverage can change if you change who's on your plan, where you live, or your age changed since you signed up — see what that means for your bottom line:`,
  }
}

/**
 * Pluralizes "dependent" based on count
 * @param count - The number of dependents (optional)
 * @returns "dependent" if count is 1, "dependents" otherwise
 */
export const pluralizeDependent = (count?: number): string => {
  return count === 1 ? 'dependent' : 'dependents'
}

/**
 * Gets the QLE document upload copy configuration for a given enrollment subtype
 * @param enrollmentSubtype - The enrollment event subtype (e.g., "marriage", "loss_of_coverage")
 * @returns The copy configuration object or null if the subtype is not found
 */
export const getQleDocumentUploadCopy = (
  enrollmentSubtype?: string
): QleDocumentUploadCopyConfig | undefined => {
  if (!enrollmentSubtype) {
    return undefined
  }

  return QLE_DOCUMENT_UPLOAD_COPY[enrollmentSubtype] || undefined
}

/**
 * Gets the given names of participants that were added
 * @param currentParticipants - The current list of covered participants
 * @param nextParticipants - The next list of covered participants
 * @returns Array of given names of participants that were added
 */
export const getAddedParticipantNames = (
  currentParticipants: MemberOnboardingPlanYearSummaryCoveredParticipantsItem[],
  nextParticipants: MemberOnboardingPlanYearSummaryCoveredParticipantsItem[]
): string[] => {
  const currentIds = new Set(
    currentParticipants.map((p) => p.id).filter(Boolean)
  )
  const added = nextParticipants.filter(
    (p) => p.id && !currentIds.has(p.id)
  )
  return added.map((p) => p.given_name as string).filter(Boolean)
}

/**
 * Gets the given names of participants that were removed
 * @param currentParticipants - The current list of covered participants
 * @param nextParticipants - The next list of covered participants
 * @returns Array of given names of participants that were removed
 */
export const getRemovedParticipantNames = (
  currentParticipants: MemberOnboardingPlanYearSummaryCoveredParticipantsItem[],
  nextParticipants: MemberOnboardingPlanYearSummaryCoveredParticipantsItem[]
): string[] => {
  const nextIds = new Set(
    nextParticipants.map((p) => p.id).filter(Boolean)
  )
  const removed = currentParticipants.filter(
    (p) => p.id && !nextIds.has(p.id)
  )
  return removed.map((p) => p.given_name as string).filter(Boolean)
}
